/**
 * Provider order is load-bearing.
 *
 *   ThemeProvider          — outermost: owns `data-theme`, needed even by the
 *                            pre-auth screens (login is a themed surface too).
 *   QueryClientProvider    — must wrap anything that queries.
 *   SessionProvider        — INSIDE the query provider, because signing out must be
 *                            able to clear the query cache, and OUTSIDE the router,
 *                            because the route guards read the session.
 *   RouterProvider         — innermost.
 *
 * The QueryClient is created once, in a ref-stable `useState` initialiser rather
 * than at module scope: a module-scope client is shared across test files and
 * carries one test's cache into the next.
 */

import { useState } from 'react';
import { RouterProvider } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { createQueryClient } from './lib/query-client';
import { ThemeProvider } from './lib/theme';
import { SessionProvider } from './auth/session';
import { createRouter } from './routes';

export function App(): React.JSX.Element {
  const [queryClient] = useState(() => createQueryClient());
  const [router] = useState(() => createRouter());

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <SessionProvider>
          <RouterProvider router={router} />
        </SessionProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}
